import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import BackButton from '../../../components/back-button/BackButton';
import Loader from '../../../components/loader/Loader';
import UploadAttachmentsModal from '../../../components/upload-image-modal/UploadAttachmentsModal';
import UploadImage from '../../../components/svgs/UploadImage.svg';
import OnFarmAnimalInfoForm from './OnFarmAnimalInfoForm';
import {
  deleteOnFarmAnimalAttachments,
  resolveOnFarmAttachments,
  uploadOnFarmAnimalAttachment,
} from '../../../api/onFarmAnimalsApi';
import { getOnFarmAnimalById, updateOnFarmAnimal } from '../../../services/onFarmInventory.service';
import { showToast } from '../../../services/toast.service';
import { ANIMAL_CONSTANTS, BUTTON_CONSTANTS } from '../../../constants/common';
import { ROUTE_PATHS } from '../../../constants/routePaths';
import { PromiseError, apiErrorTitles, toastMessages } from '../../../constants/errorMessages';
import { Animal, OnFarmAnimal } from '../../../types/interfaces/animal.interfaces';
import { Genders } from '../../../types/enums/genders.enum';

const OnFarmEditAnimal = (): JSX.Element => {
  const { animalId } = useParams();
  const navigate = useNavigate();

  const [validate, setValidate] = useState(false);
  const [animalFormIsValid, setAnimalFormIsValid] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [stateWasModified, setStateWasModified] = useState(false);
  const [updatingAnimal, setUpdatingAnimal] = useState(true);
  const [isUploadImageModalOpen, setIsUploadImageModalOpen] = useState(false);
  const [selectedAttachmentId, setSelectedAttachmentId] = useState<number | null>(null);
  const [animalInfo, setAnimalInfo] = useState<Animal>({
    name: '',
    gender: Genders.Default,
    birthDate: undefined,
    sire: '',
    dam: '',
    dna: '',
    code: '',
    registration: '',
    description: '',
    breed: { name: '', species: { name: '' } },
    attachments: [],
  });

  const attachments = animalInfo.attachments ?? [];
  const selectedAttachment = attachments.find(attachment => attachment.attachmentId === selectedAttachmentId) ?? attachments[0];

  useEffect(() => {
    if (!attachments.length) {
      setSelectedAttachmentId(null);
      return;
    }
    if (!attachments.some(attachment => attachment.attachmentId === selectedAttachmentId)) {
      setSelectedAttachmentId(attachments[0].attachmentId);
    }
  }, [attachments, selectedAttachmentId]);

  const inventoryItemPath = `${ROUTE_PATHS.APP_ON_FARM_INVENTORY}/${animalId}`;

  const getAnimalInfo = useCallback(async () => {
    if (!animalId) return;

    try {
      setIsLoading(true);
      const { data } = await getOnFarmAnimalById(animalId, { include: 'Breed.Species, Attachments' });
      setAnimalInfo({
        ...data,
        attachments: await resolveOnFarmAttachments(animalId, data.attachments),
      });
    } catch {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    } finally {
      setIsLoading(false);
    }
  }, [animalId]);

  useEffect(() => {
    getAnimalInfo();
  }, [getAnimalInfo]);

  const checkValidity = () => stateWasModified && animalFormIsValid;

  const updateAnimal = async () => {
    if (!updatingAnimal || !animalId) return;

    setUpdatingAnimal(false);

    if (!checkValidity()) {
      setValidate(true);
      setUpdatingAnimal(true);
      return;
    }

    const onFarmAnimal = animalInfo as OnFarmAnimal;

    try {
      await updateOnFarmAnimal(Number(animalId), {
        onFarmAnimalId: onFarmAnimal.onFarmAnimalId ?? Number(animalId),
        name: animalInfo.name,
        code: animalInfo.code,
        dna: animalInfo.dna,
        registration: animalInfo.registration,
        gender: animalInfo.gender,
        birthDate: animalInfo.birthDate,
        deathDate: animalInfo.deathDate,
        sire: animalInfo.sire,
        dam: animalInfo.dam,
        description: animalInfo.description,
        currentStatus: onFarmAnimal.currentStatus,
        earTag: animalInfo.earTag?.trim(),
        scrapieTag: animalInfo.scrapieTag?.trim(),
        rfidEid: animalInfo.rfidEid?.trim(),
        breedId: animalInfo.breed?.breedId ?? 0,
      });
      setValidate(false);
      showToast.success(toastMessages.ANIMAL_EDITED);
      navigate(inventoryItemPath);
    } catch (error: any) {
      if (error.code === PromiseError.NETWORK) {
        showToast.error(toastMessages.NETWORK);
      } else {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      }
    } finally {
      setUpdatingAnimal(true);
    }
  };

  const handlePostImage = async (image: FormData) => {
    try {
      if (!animalId) throw new Error('Animal id is not defined');
      await uploadOnFarmAnimalAttachment(animalId, image);
      setIsUploadImageModalOpen(false);
      showToast.success(toastMessages.ANIMAL_IMAGE_ADDED);
      await getAnimalInfo();
    } catch (error: any) {
      const errorTitle = error?.response?.data?.title;
      if (errorTitle === apiErrorTitles.BAD_FILE_TYPE) {
        showToast.error(toastMessages.INVALID_FILE_TYPE);
      } else if (error.code === PromiseError.NETWORK) {
        showToast.error(toastMessages.NETWORK);
      } else {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      }
      showToast.error(toastMessages.FAILED_TO_ADD_ANIMAL_IMAGE);
    }
  };

  const handleDeleteAttachments = async (attachmentIds: number[]) => {
    if (!animalId) throw new Error('Animal id is not defined');
    try {
      await deleteOnFarmAnimalAttachments(animalId, attachmentIds);
      await getAnimalInfo();
    } catch {
      showToast.error(toastMessages.FAILED_TO_REMOVE_ANIMAL_IMAGE);
    }
  };

  return (
    <div className="on-farm-edit-animal">
      <BackButton />
      <div className="card">
        {isLoading ? (
          <Loader loaderSize="small" simple />
        ) : (
          <>
            <UploadAttachmentsModal
              isOpen={isUploadImageModalOpen}
              onClose={() => setIsUploadImageModalOpen(false)}
              postAttachments={handlePostImage}
              existingAttachments={attachments}
              animalId={animalId!}
              getAttachmentURL={(_animalId, _attachmentId, accessToken) => accessToken}
              handleDeleteAttachments={handleDeleteAttachments}
              handleSubmitNavigation={() => setIsUploadImageModalOpen(false)}
            />
            <div className="row">
              <h3>{ANIMAL_CONSTANTS.ANIMAL_INFO}</h3>
            </div>
            <div className="wrapper">
              <div className="on-farm-animal-photos">
                <div className="on-farm-animal-main-photo">
                  <button
                    type="button"
                    className="upload-image-button"
                    onClick={() => setIsUploadImageModalOpen(true)}
                    aria-label={BUTTON_CONSTANTS.CHOOSE_FILES}>
                    <UploadImage className="upload-image-icon" />
                  </button>
                  {animalId && selectedAttachment ? (
                    <img src={selectedAttachment.accessToken} alt="Animal Photo" />
                  ) : (
                    <div className="no-animal-image">{ANIMAL_CONSTANTS.NO_IMAGE}</div>
                  )}
                </div>
                {animalId && attachments.length > 1 && (
                  <div className="on-farm-animal-photos-row">
                    {attachments.map(attachment => (
                      <button
                        key={attachment.attachmentId}
                        type="button"
                        className={`photo-thumbnail${
                          selectedAttachment?.attachmentId === attachment.attachmentId ? ' selected' : ''
                        }`}
                        onClick={() => setSelectedAttachmentId(attachment.attachmentId)}>
                        <img src={attachment.accessToken} alt="Animal Photo" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <OnFarmAnimalInfoForm
                animalInfo={animalInfo}
                setAnimalInfo={setAnimalInfo}
                validate={validate}
                setValid={setAnimalFormIsValid}
                startsValid
                setStateWasModified={setStateWasModified}
              />
            </div>
            <div className="buttons">
              <button type="button" onClick={() => navigate(inventoryItemPath)} className="small green button inverted">
                {BUTTON_CONSTANTS.CANCEL}
              </button>
              <button
                type="button"
                className={!checkValidity() ? 'small green button update-button disabled' : 'button small green update-button'}
                onClick={updateAnimal}>
                {BUTTON_CONSTANTS.UPDATE}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default OnFarmEditAnimal;
